import { useState } from "react";
import api from "../api";
import { useConfirm } from "./ConfirmDialog";

function fmtDate(d) {
  if (!d) return "";
  const p = d.split("-");
  return p[1] + "/" + p[2];
}

export default function TasksPanel({ state, setState, isAdmin }) {
  const confirm = useConfirm();
  const [tInput, setTInput] = useState("");
  const [tOwner, setTOwner] = useState(isAdmin ? "MH" : "WH");
  const [tDue, setTDue] = useState("");
  const [tPri, setTPri] = useState(false);
  const [fOwner, setFOwner] = useState("all");
  const [fSort, setFSort] = useState("dateAsc");
  const [priFilter, setPriFilter] = useState(false);
  const [editing, setEditing] = useState(null);
  const [edit, setEdit] = useState({});
  const [archiveOpen, setArchiveOpen] = useState(false);

  const today = new Date().toISOString().slice(0, 10);

  const replaceTasks = (tasks) => setState({ ...state, tasks });

  const addTask = async () => {
    if (!tInput.trim()) return;
    const body = { t: tInput.trim(), owner: tOwner, due: tDue, pri: tPri, done: false };
    await api.post("/tasks", body);
    replaceTasks([...state.tasks, body]);
    setTInput("");
    setTDue("");
    setTPri(false);
  };

  const toggleDone = async (i) => {
    const cur = state.tasks[i];
    const { data } = await api.patch(`/tasks/${i}`, { done: !cur.done });
    replaceTasks(state.tasks.map((x, j) => (j === i ? { ...x, ...data } : x)));
  };

  const delTask = async (i) => {
    const t = state.tasks[i];
    const ok = await confirm({
      title: "Delete this task?",
      message: `"${t?.t || ""}"\n\nThis cannot be undone.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    await api.delete(`/tasks/${i}`);
    replaceTasks(state.tasks.filter((_, j) => j !== i));
  };

  const startEdit = (i) => {
    setEditing(i);
    setEdit({ ...state.tasks[i] });
  };

  const saveEdit = async () => {
    const i = editing;
    const { data } = await api.patch(`/tasks/${i}`, edit);
    replaceTasks(state.tasks.map((x, j) => (j === i ? { ...x, ...data } : x)));
    setEditing(null);
    setEdit({});
  };

  let rows = state.tasks.map((t, i) => ({ t, i }));
  if (fOwner !== "all") rows = rows.filter((r) => r.t.owner === fOwner);
  if (priFilter) rows = rows.filter((r) => r.t.pri);
  rows.sort((a, b) => {
    const da = a.t.due || "9999-12-31";
    const db = b.t.due || "9999-12-31";
    return fSort === "dateAsc" ? da.localeCompare(db) : db.localeCompare(da);
  });
  const activeRows = rows.filter((r) => !r.t.done);
  const archivedRows = rows.filter((r) => r.t.done);

  const renderRow = ({ t, i }) => {
    const overdue = t.due && !t.done && t.due < today;
    if (editing === i) {
      return (
        <div className="item" key={t.id || i} data-testid={`task-${i}-edit`}>
          <div className="check" />
          <div className="item-main">
            <input
              className="inline-edit"
              value={edit.t}
              onChange={(e) => setEdit({ ...edit, t: e.target.value })}
            />
            <div className="edit-controls">
              <select
                value={edit.owner}
                onChange={(e) => setEdit({ ...edit, owner: e.target.value })}
              >
                <option value="MH">Marco</option>
                <option value="WH">Ward</option>
              </select>
              <input
                type="date"
                value={edit.due || ""}
                onChange={(e) => setEdit({ ...edit, due: e.target.value })}
              />
              <label>
                <input
                  type="checkbox"
                  checked={!!edit.pri}
                  onChange={(e) => setEdit({ ...edit, pri: e.target.checked })}
                />{" "}
                High priority
              </label>
              <button className="btn btn-sm" onClick={saveEdit}>
                Save
              </button>
            </div>
          </div>
        </div>
      );
    }
    const canCheck = isAdmin || t.owner === "WH";
    return (
      <div className={`item ${t.done ? "task-done" : ""}`} key={t.id || i} data-testid={`task-${i}`}>
        <div
          className={`check ${t.done ? "checked" : ""}`}
          onClick={() => canCheck && toggleDone(i)}
          style={!canCheck ? { cursor: "not-allowed", opacity: 0.55 } : {}}
          title={canCheck ? "Toggle complete" : "Only Marco can mark his own tasks complete"}
          data-testid={`task-${i}-check`}
        />
        <div className="item-main">
          <div className="item-title">{t.t}</div>
          <div className="task-meta">
            <span className={`task-owner owner-${t.owner}`}>
              {t.owner === "MH" ? "Marco" : "Ward"}
            </span>
            {t.due && (
              <span className={`task-due ${overdue ? "overdue" : ""}`}>
                {overdue ? "Overdue · " : ""}{fmtDate(t.due)}
              </span>
            )}
            {t.pri && <span className="task-pri">High priority</span>}
          </div>
        </div>
        {isAdmin && (
          <>
            <button
              className="edit-pencil"
              onClick={() => startEdit(i)}
              data-testid={`task-${i}-edit-btn`}
            >
              ✎
            </button>
            <button
              className="del-x"
              onClick={() => delTask(i)}
              data-testid={`task-${i}-delete`}
            >
              ×
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <div data-testid="panel-tasks">
      <div className="sec-head">Active Tasks &amp; Deliverables</div>
      <div className="sec-desc">Near-term to-dos. Check off as completed.</div>

      <div className="add-row">
        <input
          className="grow"
          placeholder="New task description..."
          value={tInput}
          onChange={(e) => setTInput(e.target.value)}
          data-testid="task-input"
        />
        <select
          value={tOwner}
          onChange={(e) => setTOwner(e.target.value)}
          data-testid="task-owner"
        >
          <option value="MH">Marco</option>
          <option value="WH">Ward</option>
        </select>
        <input
          type="date"
          value={tDue}
          onChange={(e) => setTDue(e.target.value)}
          title="Due date"
          data-testid="task-due"
        />
        <label>
          <input
            type="checkbox"
            checked={tPri}
            onChange={(e) => setTPri(e.target.checked)}
            data-testid="task-pri"
          />{" "}
          High priority
        </label>
        <button className="btn" onClick={addTask} data-testid="task-add">
          Add
        </button>
      </div>

      <div className="filter-row">
        <span className="flabel">Owner</span>
        <select value={fOwner} onChange={(e) => setFOwner(e.target.value)} data-testid="filter-owner">
          <option value="all">All</option>
          <option value="MH">Marco</option>
          <option value="WH">Ward</option>
        </select>
        <span className="flabel">Sort</span>
        <select value={fSort} onChange={(e) => setFSort(e.target.value)} data-testid="filter-sort">
          <option value="dateAsc">Due date ↑</option>
          <option value="dateDesc">Due date ↓</option>
        </select>
        <button
          className={`chip-toggle ${priFilter ? "on" : ""}`}
          onClick={() => setPriFilter((v) => !v)}
          data-testid="filter-pri"
        >
          High priority only
        </button>
      </div>

      {activeRows.length === 0 ? (
        <div className="empty-note">No active tasks match the current filters.</div>
      ) : (
        activeRows.map(renderRow)
      )}

      {archivedRows.length > 0 && (
        <div className="archive-section" data-testid="archive-section">
          <button
            className="archive-toggle"
            onClick={() => setArchiveOpen((v) => !v)}
            data-testid="archive-toggle"
          >
            <span>
              {archiveOpen ? "▾" : "▸"} Archive
              <span className="archive-count">{archivedRows.length} completed</span>
            </span>
          </button>
          {archiveOpen && (
            <div className="archive-list" data-testid="archive-list">
              {archivedRows.map(renderRow)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
